import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../AppIcon';
import { useCurrency } from '../../features/currency/CurrencyContext';
import { POPULAR_CODES } from '../../lib/currency';

const CurrencySwitcher = ({ className = '', compact = false }) => {
  const {
    currency,
    setCurrency,
    currencyCodes,
    currencies,
    rates,
    detected,
    usdToUgx,
    requestLocation,
    locationStatus,
  } = useCurrency();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = currencyCodes.filter((code) => CURRENCIES_HAS(currencies, code));
    if (!q) {
      const popular = list.filter((c) => POPULAR_CODES.includes(c));
      const rest = list.filter((c) => !POPULAR_CODES.includes(c));
      return [...popular, ...rest];
    }
    return list.filter((code) => {
      const meta = currencies[code] || {};
      return (
        code.toLowerCase().includes(q) ||
        String(meta.name || '').toLowerCase().includes(q)
      );
    });
  }, [query, currencyCodes, currencies]);

  const ugxPerOne = Number(rates?.[currency]) > 0 && Number(rates?.UGX) > 0 && currency !== 'UGX'
    ? Number(rates.UGX) / Number(rates[currency])
    : null;

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-lg border border-border bg-background hover:bg-muted/60 transition-colors font-semibold text-foreground ${
          compact ? 'px-2 py-1.5 text-xs' : 'px-2.5 py-2 text-sm'
        }`}
        aria-label="Select currency"
        title={detected?.country ? `Prices in ${currency} · detected ${detected.country}` : `Display currency ${currency}`}
      >
        <Icon name="Coins" size={compact ? 14 : 16} className="text-primary" />
        <span>{currency}</span>
        <Icon
          name="ChevronDown"
          size={14}
          className={`text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 rounded-xl border border-border bg-card shadow-lg z-50 overflow-hidden animate-slide-down">
          <div className="px-3 py-2 border-b border-border bg-muted/40">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Display currency
            </p>
            {detected?.country && (
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Visiting from {detected.country}
                {detected.currency ? ` · local ${detected.currency}` : ''}
              </p>
            )}
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search USD, Euro, Kenya…"
              className="mt-2 w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <ul className="py-1 max-h-72 overflow-y-auto">
            {filtered.map((code) => {
              const meta = currencies[code];
              const active = currency === code;
              return (
                <li key={code}>
                  <button
                    type="button"
                    onClick={() => {
                      setCurrency(code);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors ${
                      active
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="w-9 text-xs font-bold tabular-nums">{code}</span>
                      <span className="text-xs text-muted-foreground truncate">{meta?.name}</span>
                    </span>
                    {active && <Icon name="Check" size={14} />}
                  </button>
                </li>
              );
            })}
            {filtered.length === 0 && (
              <li className="px-3 py-3 text-xs text-muted-foreground">No matching currency</li>
            )}
          </ul>
          <div className="px-3 py-2 border-t border-border bg-muted/30 space-y-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                requestLocation({ force: true });
              }}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-background px-2 py-1.5 text-[11px] font-semibold text-foreground hover:bg-muted"
            >
              <Icon name="MapPin" size={12} className="text-primary" />
              {locationStatus === 'requesting' ? 'Finding your country…' : 'Use my location'}
            </button>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              {currency === 'UGX'
                ? `1 USD ≈ ${Number(usdToUgx).toLocaleString()} UGX · live rates`
                : ugxPerOne
                  ? `1 ${currency} ≈ ${Math.round(ugxPerOne).toLocaleString()} UGX · live rates`
                  : 'Prices convert with live exchange rates'}
            </p>
            <p className="text-[10px] text-muted-foreground">
              Your pick stays saved on this device.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

function CURRENCIES_HAS(map, code) {
  return Boolean(map && map[code]);
}

export default CurrencySwitcher;
